import { useEffect } from "react"
import { Button } from "@/components/ui/Button"
import type { Market } from "@/lib/useMarket"
import { panelView } from "./lib/panelState"
import MarketList from "./MarketList"
import PanelBody from "./PanelBody"
import PanelHeader from "./PanelHeader"
import PanelStrip from "./PanelStrip"
import { useQuickView } from "./useQuickView"

interface Props {
  externalId: string
  market: Market | null
  markets: Market[]
  onMarketChange: (market: Market) => void
  onClose: () => void
}

// A full-height 560px panel on the right, over the top bar; its own navy strip
// stands in for the bar on that side. The backdrop is see-through and closes
// the panel on click; the Inventory grids lift their product column above it
// so another product can be clicked while it is open. Escape closes it too.
export default function QuickViewPanel({ externalId, market, markets, onMarketChange, onClose }: Props) {
  const { data, loading, failed, reload } = useQuickView(externalId, market?.slug ?? null)
  const view = panelView(market, { loading, failed, ready: data !== null })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Radix menus and popovers handle their own Escape and mark it handled.
      if (e.key === "Escape" && !e.defaultPrevented) onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-label="Product quick view"
        className="fixed right-0 top-0 z-50 flex h-full w-[560px] max-w-full flex-col border-l border-border bg-panel-background shadow-lg"
      >
        <PanelStrip market={market} markets={markets} onMarketChange={onMarketChange} onClose={onClose} />
        <div className="flex-1 overflow-y-auto">
          <PanelHeader externalId={externalId} data={data} ready={view === "ready"} />
          {view === "pick_market" && <MarketList markets={markets} onPick={onMarketChange} />}
          {view === "failed" && (
            <div className="flex flex-col items-start gap-3 px-5 py-4">
              <p className="text-sm text-foreground">Could not load this product.</p>
              <Button variant="outline" size="sm" onClick={reload}>Try again</Button>
            </div>
          )}
          {view === "loading" && <p className="px-5 py-4 text-sm text-foreground">Loading…</p>}
          {view === "ready" && data && market && <PanelBody data={data} market={market} />}
        </div>
      </aside>
    </>
  )
}
